/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { api } from "core/api";
import { Card, Button, SvgIcon } from "core/components";
import { InventoryItemFormData, ProjectFormData, ProjectStatus, TaskFormData } from "core/schema";
import { material_requests_admin_project_path, purchase_requests_admin_project_path } from "routes";
import ProjectMaterialRequests from "./ProjectMaterialRequests";
import ProjectPurchaseRequests from "./ProjectPurchaseRequests";
import ProjectRequisitionForm from "./ProjectRequisitionForm";

type ProjectRequisitionsProps = {
  project: ProjectFormData;
  task: TaskFormData;
  inventory_items: Array<InventoryItemFormData>;
};

const ProjectRequisitions = ({
  project,
  task,
  inventory_items,
  isLoading,
  can_approve_reject,
  can_delete,
  can_request
}: ProjectRequisitionsProps): JSX.Element => {
  const [loadingMaterialRequests, setLoadingMaterialRequests] = React.useState(false);
  const [loadingPurchaseRequests, setLoadingPurchaseRequests] = React.useState(false);
  const [materialRequests, setMaterialRequests] = React.useState([]);
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [showRequisitions, setShowRequisitions] = React.useState(true);
  const policy = project.policy;

  const getMaterialRequests = async () => {
    try {
      setLoadingMaterialRequests(true);
      const response = await api.get(material_requests_admin_project_path({ id: project.id }));
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMaterialRequests(false);
    }
  };

  const getPurchaseRequests = async () => {
    try {
      setLoadingPurchaseRequests(true);
      const response = await api.get(purchase_requests_admin_project_path({ id: project.id }));
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPurchaseRequests(false);
    }
  };

  const getRequests = React.useCallback(async () => {
    setMaterialRequests(await getMaterialRequests());
    setPurchaseRequests(await getPurchaseRequests());
  }, []);

  React.useEffect(() => {
    if (showRequisitions) {
      getRequests().catch(console.error);
    }
  }, [showRequisitions]);

  const hasRequisitions = () => {
    return materialRequests.length > 0 || purchaseRequests.length > 0;
  };

  const onSubmit = () => {
    setShowRequisitions(!showRequisitions);
  };

  const onCancel = () => {
    setShowRequisitions(!showRequisitions);
  };

  const canShowRequisitions = () => {
    return project.status == ProjectStatus.Punchlisting || project.status == ProjectStatus.Completed
      ? can_request
      : project.status == ProjectStatus.OnGoing;
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No requisitions yet.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  return (
    <Card.Body>
      <Container fluid={true}>
        <Row className="mb-2">
          <Col lg={12} className="d-flex justify-content-end">
            {showRequisitions && canShowRequisitions() && policy.can_save && (
              <Button
                className="button--brand"
                icon="plus"
                title="New Requisition"
                type="button"
                onClick={() => {
                  setShowRequisitions(!showRequisitions);
                }}
              />
            )}
          </Col>
        </Row>
        <Row>
          {showRequisitions ? (
            <>
              {hasRequisitions() ? (
                <>
                  <Col>
                    <ProjectMaterialRequests
                      project={project}
                      loading={loadingMaterialRequests}
                      material_requests={materialRequests}
                      getMaterialRequests={getMaterialRequests}
                      can_approve_reject={can_approve_reject}
                      can_delete={can_delete}
                    />
                  </Col>
                  <Col>
                    <ProjectPurchaseRequests
                      project={project}
                      loading={loadingPurchaseRequests}
                      purchase_requests={purchaseRequests}
                      getPurchaseRequests={getPurchaseRequests}
                      can_approve_reject={can_approve_reject}
                      can_delete={can_delete}
                    />
                  </Col>
                </>
              ) : (
                noContent()
              )}
            </>
          ) : (
            <ProjectRequisitionForm
              project={project}
              task={task}
              inventory_items={inventory_items}
              isItemsLoading={isLoading}
              onCancel={onCancel}
              onSubmit={onSubmit}
            />
          )}
        </Row>
      </Container>
    </Card.Body>
  );
};

export default ProjectRequisitions;
